import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import i18n from '../../i18n';
import { Dish, Order, RiskEvent, RiskOrder, SourceType } from '../../models';
import apiManage from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import { accountState } from '../my-account/account-state';
import OrderDetailDialog from '../data-analysis/order-data/order-detail-dialog';
import {
    dateFormat,
    formatEatTypeText,
    formatEventTypeText,
    formatRiskMoney,
    formatRiskMinutes,
    formatRiskStatusText,
    getRiskStatusColor,
} from '../../constants/tool-order';
import { ReactComponent as RiskWarningTriangleIcon } from '../../static/images/svg/risk-order-row-warning.svg';
import { RiskDetailDialogFooter, type RiskDetailDialogActionStatus } from './risk-detail-dialog-footer';
import { resolveEventRiskStatus } from './risk-event-detail-utils';
import {
    canOpenRiskAssociatedOrderDetail,
    fetchRiskAssociatedOrderDetail,
    mapRiskOrderToOrderDetailFallback,
} from './risk-associated-order-detail';


interface RiskEventDetailDialogProps {
    open?: boolean;
    onClose?: () => void;
    eventSummary?: RiskEvent | null;
    stringId?: string;
    onSaved?: () => void;
}

/** 关联订单表头（与导出 Excel / 原 CardFrom 表格列一致） */
const ASSOCIATED_ORDER_HEAD_CELLS: HeadCell[] = [
    { id: 'order_head_id', label: i18n.t('business_analysis_page_order_account'), minWidth: '100px' },
    { id: 'table_name', label: i18n.t('business_analysis_page_table_name'), minWidth: '88px' },
    { id: 'people_count', label: i18n.t('immediate_number_people'), minWidth: '72px' },
    { id: 'dish_count', label: i18n.t('intelligent_risk_control_page_column_dish_count'), minWidth: '88px' },
    { id: 'operator_name', label: i18n.t('intelligent_risk_control_page_column_server'), minWidth: '96px' },
    {
        id: 'open_time',
        label: i18n.t('immediate_table_opening_time'),
        minWidth: '140px',
        renderFunction: (row) => formatEventDetailDateTime(row.open_time),
    },
    {
        id: 'close_time',
        label: i18n.t('immediate_statement_time'),
        minWidth: '140px',
        renderFunction: (row) => formatEventDetailDateTime(row.close_time),
    },
    {
        id: 'amount',
        label: i18n.t('intelligent_risk_control_page_column_amount_original_actual'),
        minWidth: '140px',
        renderFunction: (row) => `${formatRiskMoney(row.origin_amount)} / ${formatRiskMoney(row.actual_amount)}`,
    },
    {
        id: 'discount_amount',
        label: i18n.t('intelligent_risk_control_page_column_discount_amount'),
        minWidth: '96px',
        renderFunction: (row) => formatRiskMoney(row.discount_amount),
    },
    { id: 'refund_dish_count', label: i18n.t('intelligent_risk_control_page_column_refund_count'), minWidth: '88px' },
    {
        id: 'refund_amount',
        label: i18n.t('intelligent_risk_control_page_column_refund_amount'),
        minWidth: '96px',
        renderFunction: (row) => formatRiskMoney(row.refund_amount),
    },
    { id: 'avg_dish_per_person', label: i18n.t('intelligent_risk_control_page_column_avg_dish_count'), minWidth: '96px' },
    {
        id: 'dining_duration_min',
        label: i18n.t('intelligent_risk_control_page_column_duration_minutes'),
        minWidth: '96px',
        renderFunction: (row) => formatRiskMinutes(row.dining_duration_min),
    },
];

/**
 * 事件详情内日期时间展示。
 * @param val - ISO 或后端时间字符串
 */
function formatEventDetailDateTime(val?: string) {
    if (!val) return '-';
    return dateFormat(val, 'HH:mm DD/MM/YYYY');
}

type WarningSideProps = {
    label: string;
    value?: React.ReactNode;
};

type WarningSideAlign = 'start' | 'end';

/**
 * 警示条单侧：三角 + 标签 + 数值（与订单详情弹窗一致）。
 */
const RiskEventDialogWarningSide: React.FC<WarningSideProps & { align: WarningSideAlign }> = ({
    label,
    value,
    align,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flex: 1,
            justifyContent: align === 'start' ? 'flex-start' : 'flex-end',
        }}
    >
        <Box
            sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                color: 'var(--func-error)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            aria-hidden
        >
            <RiskWarningTriangleIcon width={24} height={24} />
        </Box>
        <Typography variant="body2" sx={{ color: 'var(--func-error)' }}>
            {label}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: 'var(--func-error)' }}>
            {value ?? '-'}
        </Typography>
    </Box>
);

/**
 * 风险行为事件详情弹窗：布局与风控订单详情一致（事件摘要 + 警示条 + 关联订单表 + 底部双按钮）。
 */
const RiskEventDetailDialog: React.FC<RiskEventDetailDialogProps> = observer((props) => {
    const { open, onClose, eventSummary, stringId, onSaved } = props;
    /** 底部操作提交中：1 忽略 / 2 确认已处理 */
    const [actionSubmitting, setActionSubmitting] = useState<RiskDetailDialogActionStatus>(null);
    /** 关联订单公共订单详情弹窗是否打开 */
    const [isOrderDetailOpen, setIsOrderDetailOpen] = useState(false);
    /** 订单详情摘要（order_head） */
    const [orderDetailSummary, setOrderDetailSummary] = useState<Order | null>(null);
    /** 订单详情菜品列表 */
    const [orderDetailDishes, setOrderDetailDishes] = useState<Dish[]>([]);
    /** 订单详情加载中 */
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    /** 即时风控事件详情内关联订单均为即时单，用 order_head_id 拉详情 */
    const isInstantAssociatedOrder = true;

    /** 关闭弹窗 */
    const handleClose = () => {
        onClose?.();
    };

    /** 关闭嵌套订单详情弹窗 */
    const handleCloseOrderDetail = useCallback(() => {
        setIsOrderDetailOpen(false);
        setOrderDetailSummary(null);
        setOrderDetailDishes([]);
        setOrderDetailLoading(false);
    }, []);

    /**
     * 关联订单表行点击：打开公共 OrderDetailDialog（与退菜列表 onRowClick 一致）。
     * @param row - 关联订单行
     */
    const handleOpenOrderDetailFromRow = useCallback(
        async (row: RiskOrder) => {
            if (!stringId || !canOpenRiskAssociatedOrderDetail(row, isInstantAssociatedOrder)) {
                return;
            }
            const fallback = mapRiskOrderToOrderDetailFallback(row);
            setOrderDetailSummary(fallback);
            setOrderDetailDishes([]);
            setIsOrderDetailOpen(true);
            setOrderDetailLoading(true);
            const { summary, dishes, hasError } = await fetchRiskAssociatedOrderDetail(
                stringId,
                row,
                isInstantAssociatedOrder
            );
            setOrderDetailSummary(summary);
            setOrderDetailDishes(dishes);
            setOrderDetailLoading(false);
            if (hasError) {
                GlobalNotification.error(i18n.t('immediate_get_order_details_failed'));
            }
        },
        [stringId, isInstantAssociatedOrder]
    );

    /**
     * 提交处理结果（底部按钮：1 已忽略 / 2 已解决）。
     * @param nextStatus - 目标风控状态
     */
    const performSave = async (nextStatus: number) => {
        if (!stringId || !eventSummary?.eventId) return;
        if (actionSubmitting !== null) return;
        if (nextStatus === 0) {
            GlobalNotification.error(i18n.t('common_text_cannot_update_status_to_pending_confirmation'));
            return;
        }
        const submittingStatus = nextStatus === 1 ? 1 : 2;
        setActionSubmitting(submittingStatus);
        try {
            const res = await apiManage.patch_restaurant_risk_handle(stringId, {
                ids: [eventSummary.eventId],
                status: nextStatus,
                target_type: SourceType.EVENT,
                operator_name: accountState.userInfo?.nickname ?? '',
            });

            if (!res?.error) {
                GlobalNotification.success(i18n.t('general_text_modify_success'));
                onSaved?.();
                onClose?.();
            } else {
                GlobalNotification.error(i18n.t('general_text_modify_failed'));
            }
        } finally {
            setActionSubmitting(null);
        }
    };

    const eventTypeKey = formatEventTypeText(eventSummary?.eventType);
    const highlightText = eventTypeKey && eventTypeKey !== '-' ? i18n.t(eventTypeKey) : '';
    const statusCode = eventSummary ? resolveEventRiskStatus(eventSummary) : 0;
    const statusTextKey = formatRiskStatusText(statusCode);
    const statusColor = getRiskStatusColor(statusCode);
    const associatedOrders = eventSummary?.orders ?? [];

    const titleContent = eventSummary ? (
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', flexWrap: 'wrap', gap: 0 }}>
            <Typography variant="h3" component="span" className="header-title">
                {i18n.t('intelligent_risk_control_page_dialog_title_event_detail_prefix')}
            </Typography>
            {highlightText ? (
                <Typography variant="h3" component="span" sx={{ color: 'var(--func-error)' }}>
                    {highlightText}
                </Typography>
            ) : null}
            <Typography variant="h3" component="span" className="header-title">
                {i18n.t('intelligent_risk_control_page_dialog_title_event_detail_suffix')}
            </Typography>
        </Box>
    ) : undefined;

    return (
        <>
        <DialogComponent
            fullWidth
            heightSize="large"
            defaultHeaderProps={{
                titleContent,
                onClose: handleClose,
                titleAlign: 'center',
            }}
            maxWidth='xl'
            open={!!open && !isOrderDetailOpen}
            onClose={handleClose}
            footer={
                <RiskDetailDialogFooter
                    actionSubmitting={actionSubmitting}
                    onIgnore={() => void performSave(1)}
                    onConfirm={() => void performSave(2)}
                />
            }
        >
            <Box
                sx={{
                    px: 2,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {eventSummary ? (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                bgcolor: 'common.white',
                                borderRadius: '16px',
                                boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                                px: 2,
                                py: 3,
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    flexWrap: 'wrap',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-primary-300)', mb: 0.5 }}>
                                        {i18n.t('intelligent_risk_control_page_column_event_type')}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-dark)' }}>
                                        {highlightText || '-'}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-primary-300)', mb: 0.5 }}>
                                        {i18n.t('immediate_order_type')}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-dark)' }}>
                                        {eventSummary.eatType === undefined ? '-' : formatEatTypeText(eventSummary.eatType) || '-'}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 120, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-primary-300)', mb: 0.5 }}>
                                        {i18n.t('restaurant_info_page_status')}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: statusColor }}>
                                        {statusTextKey === '-' ? '-' : i18n.t(statusTextKey)}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 160, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-primary-300)', mb: 0.5 }}>
                                        {i18n.t('restaurant_extensions_page_text_create_time')}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-dark)' }}>
                                        {formatEventDetailDateTime(eventSummary.createdAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                bgcolor: 'common.white',
                                borderRadius: '12px',
                                border: '1px solid var(--func-error)',
                                p: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <RiskEventDialogWarningSide
                                align="start"
                                label={`${i18n.t('intelligent_risk_control_page_column_event_type')}：`}
                                value={highlightText || '-'}
                            />
                            <RiskEventDialogWarningSide
                                align="end"
                                label={`${i18n.t('restaurant_extensions_page_text_create_time')}：`}
                                value={formatEventDetailDateTime(eventSummary.createdAt)}
                            />
                        </Box>

                        <Box
                            sx={{
                                bgcolor: 'common.white',
                                borderRadius: '16px',
                                boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                                px: 2,
                                py: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ color: 'var(--text-primary-300)' }}>
                                {i18n.t('intelligent_risk_control_page_section_associated_orders')}
                            </Typography>
                            <TableComponent
                                isPagination
                                allCount={associatedOrders.length}
                                headCellList={ASSOCIATED_ORDER_HEAD_CELLS}
                                list={associatedOrders}
                                showChecked={0}
                                isTableLoading={false}
                                handleSort={() => {}}
                                onRowClick={(row) => {
                                    if (canOpenRiskAssociatedOrderDetail(row as RiskOrder, isInstantAssociatedOrder)) {
                                        void handleOpenOrderDetailFromRow(row as RiskOrder);
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : null}
            </Box>
        </DialogComponent>
        <OrderDetailDialog
            handleClose={handleCloseOrderDetail}
            open={isOrderDetailOpen}
            order={orderDetailSummary}
            list={orderDetailDishes}
            isTableLoading={orderDetailLoading}
        />
        </>
    );
});

export default RiskEventDetailDialog;
